/**
 * What "turn on" may reach in the layer tree. A layer hidden from the viewer
 * (`isHidden`, the eye-slash) stays off when its folder or its type is
 * turned on, and so does everything under it: hiding a folder is how a user
 * keeps a set of layers out of the scene.
 */
export interface TreeLayer {
  id: string;
  parentLayerId?: string | null;
  isHidden?: boolean | null;
}

/** Descendants of `layerId` that are not hidden and not under a hidden layer. */
export function shownSubLayerIds(layers: readonly TreeLayer[], layerId: string): string[] {
  const out: string[] = [];
  const walk = (parentId: string) => {
    for (const child of layers) {
      if (child.parentLayerId !== parentId || child.isHidden) continue;
      out.push(child.id);
      walk(child.id);
    }
  };
  walk(layerId);
  return out;
}

/** The layer or one of its ancestors is hidden. */
export function isHiddenInTree(layers: readonly TreeLayer[], layer: TreeLayer): boolean {
  const seen = new Set<string>();
  let cur: TreeLayer | undefined = layer;
  while (cur && !seen.has(cur.id)) {
    if (cur.isHidden) return true;
    seen.add(cur.id);
    const parentId: string | null | undefined = cur.parentLayerId;
    cur = parentId ? layers.find((l) => l.id === parentId) : undefined;
  }
  return false;
}

/** Ids of every layer that is hidden itself or sits under a hidden layer. */
export function hiddenInTreeIds(layers: readonly TreeLayer[]): Set<string> {
  const byId = new Map(layers.map((l) => [l.id, l]));
  const memo = new Map<string, boolean>();
  const hidden = (l: TreeLayer, depth = 0): boolean => {
    const known = memo.get(l.id);
    if (known !== undefined) return known;
    const parent = l.parentLayerId ? byId.get(l.parentLayerId) : undefined;
    // depth guard: a parentLayerId cycle ends here instead of recursing forever.
    const v = !!l.isHidden || (!!parent && depth < 64 && hidden(parent, depth + 1));
    memo.set(l.id, v);
    return v;
  };
  return new Set(layers.filter((l) => hidden(l)).map((l) => l.id));
}
